import { PdfTheme } from '../types/types';
import { listPdfThemes, upsertPdfTheme, deletePdfTheme as remoteDeletePdfTheme, type PdfThemeRecord } from '../services/firebaseDatabase';
import { Timestamp } from 'firebase/firestore';

const STORAGE_KEY = 'gc_pdf_themes_v1';
const ACTIVE_KEY = 'gc_pdf_active_theme_v1';

function readAll(): PdfTheme[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as PdfTheme[];
  } catch (e) {
    console.warn('Failed to read PDF themes from localStorage', e);
    return [];
  }
}

function writeAll(themes: PdfTheme[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(themes));
  } catch (e) {
    console.warn('Failed to write PDF themes to localStorage', e);
  }
}

export function listThemes(labId?: string): PdfTheme[] {
  const local = readAll().filter(t => !labId || t.labId === labId);
  // Fire-and-forget: attempt to fetch remote themes and merge into localStorage
  (async () => {
    try {
      const remote = await listPdfThemes(labId);
      if (!remote || remote.length === 0) return;
      const merged = [...local];
      for (const r of remote as PdfThemeRecord[]) {
        if (!r.id) continue;
        const exists = merged.find(m => m.id === r.id);
        const theme: PdfTheme = {
          id: r.id,
          name: r.name || 'Untitled',
          clinicName: r.clinicName,
          addressLines: r.addressLines,
          contactEmail: r.contactEmail,
          footerLeftText: r.footerLeftText,
          footerRightText: r.footerRightText,
          logoDataUrl: r.logoDataUrl,
          logoUrl: r.logoUrl,
          logoFilename: r.logoFilename,
          microscopeDataUrl: r.microscopeDataUrl,
          microscopeUrl: r.microscopeUrl,
          microscopeFilename: r.microscopeFilename,
          technicianSignDataUrl: r.technicianSignDataUrl,
          technicianSignUrl: r.technicianSignUrl,
          technicianSignFilename: r.technicianSignFilename,
          pathologistSignDataUrl: r.pathologistSignDataUrl,
          pathologistSignUrl: r.pathologistSignUrl,
          pathologistSignFilename: r.pathologistSignFilename,
          createdAt: (() => {
            const c = r.createdAt;
            if (!c) return Date.now();
            if (c instanceof Timestamp) {
              return c.toMillis();
            }
            if (typeof c === 'object' && 'toMillis' in c) {
              // Fallback for like-objects if instanceof fails (e.g. different SDK versions)
              return (c as { toMillis: () => number }).toMillis();
            }
            if (typeof c === 'number') return c;
            return Date.now();
          })(),
          labId: r.labId
        };
        if (exists) {
          // prefer remote if updatedAt is newer (best-effort)
          const idx = merged.findIndex(m => m.id === r.id);
          merged[idx] = { ...merged[idx], ...theme };
        } else {
          merged.push(theme);
        }
      }
      writeAll(merged);
    } catch {
      // ignore remote errors
    }
  })();

  return local;
}

export function getTheme(id: string): PdfTheme | undefined {
  return readAll().find(t => t.id === id);
}

export function saveTheme(theme: PdfTheme, labId?: string) {
  if (labId) {
    theme.labId = labId;
  }
  const themes = readAll();
  const idx = themes.findIndex(t => t.id === theme.id);
  // If remote URL exists, drop large base64 fields to keep localStorage small
  const compact: PdfTheme = {
    ...theme,
    logoDataUrl: theme.logoUrl ? undefined : theme.logoDataUrl,
    microscopeDataUrl: theme.microscopeUrl ? undefined : theme.microscopeDataUrl,
    technicianSignDataUrl: theme.technicianSignUrl ? undefined : theme.technicianSignDataUrl,
    pathologistSignDataUrl: theme.pathologistSignUrl ? undefined : theme.pathologistSignDataUrl,
  };
  if (idx >= 0) themes[idx] = compact;
  else themes.push(compact);
  writeAll(themes);
  // background upsert to Firestore
  (async () => {
    try {
      // Strip base64 when syncing if URL is present
      const syncTheme = {
        ...theme,
        logoDataUrl: theme.logoUrl ? undefined : theme.logoDataUrl,
        microscopeDataUrl: theme.microscopeUrl ? undefined : theme.microscopeDataUrl,
        technicianSignDataUrl: theme.technicianSignUrl ? undefined : theme.technicianSignDataUrl,
        pathologistSignDataUrl: theme.pathologistSignUrl ? undefined : theme.pathologistSignDataUrl,
      } as unknown as import('../services/firebaseDatabase').PdfThemeRecord;
      await upsertPdfTheme(syncTheme);
    } catch {
      console.warn('Failed to sync theme to Firestore');
    }
  })();
}

export function deleteTheme(id: string) {
  const themes = readAll().filter(t => t.id !== id);
  writeAll(themes);
  // background delete in Firestore
  (async () => {
    try {
      await remoteDeletePdfTheme(id);
    } catch (e) {
      console.warn('Failed to delete theme from Firestore', e);
    }
  })();
}

export function setActiveThemeId(id: string | null) {
  if (id === null) localStorage.removeItem(ACTIVE_KEY);
  else localStorage.setItem(ACTIVE_KEY, id);
}

export function getActiveThemeId(): string | null {
  return localStorage.getItem(ACTIVE_KEY);
}

export function getActiveTheme(): PdfTheme | undefined {
  const id = getActiveThemeId();
  if (!id) return undefined;
  return getTheme(id);
}

export function createEmptyTheme(name = 'Untitled Theme'): PdfTheme {
  return {
    id: `theme_${Date.now()}`,
    name,
    clinicName: 'Your Clinic Name',
    addressLines: ['Address line 1', 'City, PIN'],
    contactEmail: 'email@example.com',
    footerLeftText: '',
    footerRightText: '',
    createdAt: Date.now(),
  };
}
